import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import {
  CELL_FILTER,
  IEntityFilterItem,
  IAuditTrailFormRule,
  IAuditTrailFormRules,
  IStandWorkSheetItemItemViewAuditLog,
  WIDGET_COMPONENT,
} from '@app/shared/types';
import { WIDGET_FAMILY, TIME_FORMATS } from '@config/global-constants';
import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { chain, eq, get, gt, isEmpty, size, toLower } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { informationDataParamsCellRender } from './stand-worksheet-columns-definition';
import { StandWorksheetService } from './stand-worksheet.service';
import { toMoment } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';

export const getAuditTrailTemp = (
  service: StandWorksheetService,
  data: IStandWorkSheetItemItemViewAuditLog[],
  options: {
    user$: BehaviorSubject<IEntityFilterItem[]>;
    type: 'item' | 'information';
    loader$: BehaviorSubject<boolean>;
  } = {
    type: 'item',
    loader$: new BehaviorSubject(true),
    user$: new BehaviorSubject([]),
  },
): IAuditTrailFormRule => {
  let minHeight = `290px`;

  const operators = chain(data)
    .map('operator')
    .unionBy('value')
    .sortBy((item) => toLower(get(item, 'text')))
    .value();

  if (eq(size(operators), 4)) {
    minHeight = `320px`;
  } else if (gt(size(operators), 4)) {
    minHeight = `350px`;
  }
  const template: IAuditTrailFormRules = {
    information: {
      schema: {
        type: 'object',
        properties: {
          items: {
            type: 'array',
          },
        },
      },
      value: {
        items: data,
      },
      formRules: {
        fields: {
          items: {
            title: '',
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.edit_grid,
            widgetData: {
              filterable: true,
              showSearch: true,
              canAdd: false,
              canEdit: false,
              multiselect: false,
              disableColumnSelector: true,
              gridMinHeight: minHeight,
              gridHeight: null,
              orderControls: false,
              showLoader: options.loader$,
              columns: [
                {
                  title: I18n.Common.DateTime,
                  filterType: CELL_FILTER.dateTime,
                  path: 'date',
                  filterParams: {
                    filterOptions: ['equals', 'inRangeDate'],
                    default: {
                      type: 'inRangeDate',
                    },
                    step: 1,
                  },
                  valueGetter: (params: ValueGetterParams): string => {
                    const transactionDate = params.data.date;

                    return service.companyService.convertUTCDateWithTimeZone(
                      transactionDate,
                      true,
                      TIME_FORMATS.L_LTS,
                    );
                  },
                  defaultView: true,
                  sortable: true,
                  comparator: (valueA, valueB): any => {
                    if (valueA === valueB) {
                      return 0;
                    }

                    return toMoment(valueA).toISOString() >
                      toMoment(valueB).toISOString()
                      ? 1
                      : -1;
                  },
                },
                {
                  path: 'operator.value',
                  labelPath: 'operator.text',
                  title: I18n.Common.Operator,
                  minWidth: 120,
                  defaultView: true,
                  filterType: CELL_FILTER.entity,
                  filterParams: {
                    quickFilter: true,
                    values: isEmpty(data) ? options.user$ : operators,
                    filterOptions: ['containsAny'],
                  },
                },
                {
                  path: 'previous',
                  title: I18n.Common.Previous,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter:
                    service.numberFormatService.getNumberFormatter(true),
                  cellRendererParams: informationDataParamsCellRender(true),
                  ...COLUMN_ALIGN_RIGHT,
                },
                {
                  path: 'changed',
                  title: I18n.Common.Changed,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter:
                    service.numberFormatService.getNumberFormatter(true),
                  cellRendererParams: informationDataParamsCellRender(true),
                  ...COLUMN_ALIGN_RIGHT,
                },
              ],
              userGridOptions: {
                noGridHeight: true,
                suppressHorizontalScroll: false,
                overlayNoRowsTemplate: service.i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
              },
            },
          },
        },
      },
    },
    item: {
      value: {
        items: data,
      },
      schema: {
        type: 'object',
        properties: {
          items: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          items: {
            title: I18n.Common.UpdateForm,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.edit_grid,
            widgetData: {
              filterable: true,
              showSearch: true,
              canAdd: false,
              canEdit: false,
              multiselect: false,
              disableColumnSelector: true,
              gridMinHeight: minHeight,
              gridHeight: null,
              orderControls: false,
              showLoader: options.loader$,
              columns: [
                {
                  title: I18n.Common.DateTime,
                  filterType: CELL_FILTER.dateTime,
                  path: 'date',
                  filterParams: {
                    filterOptions: ['equals', 'inRangeDate'],
                    default: {
                      type: 'inRangeDate',
                    },
                    step: 1,
                  },
                  minWidth: 150,
                  valueGetter: (params: ValueGetterParams): string => {
                    const transactionDate = params.data.date;

                    return service.companyService.convertUTCDateWithTimeZone(
                      transactionDate,
                      true,
                      TIME_FORMATS.L_LTS,
                    );
                  },
                  defaultView: true,
                  sortable: true,
                  comparator: (valueA, valueB): any => {
                    if (valueA === valueB) {
                      return 0;
                    }

                    return toMoment(valueA).toISOString() >
                      toMoment(valueB).toISOString()
                      ? 1
                      : -1;
                  },
                },
                {
                  path: 'operator.value',
                  labelPath: 'operator.text',
                  title: I18n.Common.Operator,
                  minWidth: 120,
                  defaultView: true,
                  filterType: CELL_FILTER.entity,
                  filterParams: {
                    quickFilter: true,
                    values: isEmpty(data) ? options.user$ : operators,
                    filterOptions: ['containsAny'],
                  },
                },
                {
                  path: 'manager_begin',
                  title: I18n.Forms.StandWorksheetForm.MGRStartingOverride,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
                {
                  path: 'gratis',
                  title: I18n.Forms.StandWorksheetForm.Gratis,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
                {
                  path: 'spoilage',
                  title: I18n.Forms.StandWorksheetForm.Spoilage,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
                {
                  path: 'adjustment',
                  title: I18n.Forms.StandWorksheetForm.Adjustment,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
                {
                  path: 'end',
                  title: I18n.Forms.StandWorksheetForm.EndingCount,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
                {
                  path: 'gratis_post_closure',
                  title: I18n.Forms.StandWorksheetForm.PostClosureGratis,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  hide: !service.stateService.getStandWorksheetPostClosureSetting(),
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
                {
                  path: 'ingredient_spoil_post_closure',
                  title: I18n.Forms.StandWorksheetForm.PostClosureSpoilage,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                  hide: !service.stateService.getStandWorksheetPostClosureSetting(),
                  valueFormatter: (params: ValueFormatterParams) =>
                    service.auditTrailFormateNumber(params),
                },
              ],
              userGridOptions: {
                suppressHorizontalScroll: false,
                overlayNoRowsTemplate: service.i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
              },
            },
          },
        },
        defaultGroup: true,
      },
    },
  };

  return template[get(options, 'type')];
};
